/**
 * Format a number value using German locale (e.g., 1.234.567,89).
 * Returns '-' for null, undefined, empty string, or NaN values.
 */
export function formatNumber(
  value: number | string | null | undefined,
  decimals: number = 2
): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Format a tonnage value using German locale with exactly two decimal places.
 * Returns the en-dash '–' (U+2013) for null, undefined, empty string, or NaN values -- the
 * project-wide unified "no value" marker for empty sums (Plan 23-02, D-12/Claude's Discretion).
 * A real zero ('0') stays a zero -- only "no value" becomes the dash.
 */
export function formatTonnage(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '–';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '–';
  }

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Format a percentage value with sign using German locale (e.g., +12,5 %).
 * Input is already in percentage form (12.5 means 12.5%).
 * The value is divided by 100 for Intl.NumberFormat percent style.
 * Returns '-' for null, undefined, or NaN values.
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  return new Intl.NumberFormat('de-DE', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'always',
  }).format(value / 100);
}

/**
 * Calculate the percentage delta between current and previous values.
 * Returns null if either input is null/NaN or if previous is zero (division by zero).
 * Formula: ((current - previous) / previous) * 100
 */
export function calculateDelta(
  current: string | number | null,
  previous: string | number | null
): number | null {
  if (current === null || previous === null) {
    return null;
  }

  const curr = typeof current === 'string' ? parseFloat(current) : current;
  const prev = typeof previous === 'string' ? parseFloat(previous) : previous;

  if (isNaN(curr) || isNaN(prev) || prev === 0) {
    return null;
  }

  return ((curr - prev) / prev) * 100;
}

/**
 * Get a CSS color value based on a delta value.
 * null/zero => neutral gray.
 * positiveIsGood controls the polarity: for market evaluations a rising value is good
 * (default true, e.g. Versandmenge), for accident metrics a rising value is bad
 * (positiveIsGood=false, e.g. Unfallhäufigkeitsrate).
 */
export function getDeltaColor(delta: number | null, positiveIsGood: boolean = true): string {
  if (delta === null || delta === 0) {
    return 'var(--mantine-color-steel-5)';
  }

  const isGood = positiveIsGood ? delta > 0 : delta < 0;

  return isGood ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
}
